import { useEffect, useMemo, useState } from 'react'
import { NavLink, Navigate, Outlet, useNavigate, Link, useLocation } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { PageContainer } from '@/components/shared/page-container'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Skeleton } from '@/components/ui/skeleton'
import { ThemeToggle } from '@/components/ThemeToggle'
import { getDefaultRouteForRole } from '@/lib/auth'
import { getCurrentUserRole, signOutUser } from '@/lib/api'
import { getErrorMessage } from '@/lib/error-messages'
import { queryKeys } from '@/lib/query-keys'
import { supabase } from '@/lib/supabase'
import { Footer } from './footer'
import { cn } from '@/lib/utils'

const orderedLinks = [
  { to: '/admin/sessions', label: 'Termine' },
  { to: '/admin/courses', label: 'Kurse' },
  { to: '/admin/bookings', label: 'Buchungen' },
  { to: '/admin/customers', label: 'Teilnehmer' },
  { to: '/admin/users/staff', label: 'Mitarbeiter' },
  { to: '/admin/vouchers', label: 'Gutscheine' },
  { to: '/admin/discounts', label: 'Rabatte' },
  { to: '/admin/reports', label: 'Berichte' },
  { to: '/admin/notifications', label: 'Benachrichtigungen' },
  { to: '/admin/resources', label: 'Ressourcen' },
  { to: '/admin/settings', label: 'Einstellungen' },
]

const mobileLinks = [...orderedLinks]

function getInitialWindowWidth() {
  if (typeof window === 'undefined') {
    return 1440
  }

  return window.innerWidth
}

function getVisibleLinkCount(windowWidth: number) {
  if (windowWidth >= 1230) return 7
  if (windowWidth >= 1080) return 6
  if (windowWidth >= 980) return 5
  if (windowWidth >= 880) return 4
  if (windowWidth >= 768) return 3
  return 0
}

export function AdminLayout() {
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const [windowWidth, setWindowWidth] = useState(getInitialWindowWidth)

  const roleQuery = useQuery({
    queryKey: queryKeys.currentUserProfile,
    queryFn: getCurrentUserRole,
    retry: false,
    refetchOnWindowFocus: false,
  })

  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(() => {
      queryClient.invalidateQueries({ queryKey: queryKeys.currentUserProfile })
    })

    return () => subscription.unsubscribe()
  }, [queryClient])

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const logoutMutation = useMutation({
    mutationFn: signOutUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.currentUserProfile })
      toast.success('Erfolgreich ausgeloggt.')
      navigate('/login', { replace: true })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const { primaryLinks, collapsedLinks } = useMemo(() => {
    const visibleLinkCount = getVisibleLinkCount(windowWidth)

    return {
      primaryLinks: orderedLinks.slice(0, visibleLinkCount),
      collapsedLinks: orderedLinks.slice(visibleLinkCount),
    }
  }, [windowWidth])

  const moreMenuActive = collapsedLinks.some((link) => location.pathname.startsWith(link.to))

  if (roleQuery.isLoading) {
    return (
      <PageContainer className="py-12">
        <Skeleton className="h-8 w-64" />
      </PageContainer>
    )
  }

  if (!roleQuery.data || !roleQuery.data.is_active) {
    return <Navigate to="/login" replace />
  }

  if (roleQuery.data.role !== 'admin') {
    return <Navigate to={getDefaultRouteForRole(roleQuery.data.role)} replace />
  }

  return (
    <div className="h-dvh overflow-hidden font-body text-foreground flex flex-col">
      <header className="glass sticky top-0 z-50 border-b border-border/50 shadow-lg">
        <PageContainer className="flex h-16 items-center justify-between">
          <div className="flex min-w-0 items-center gap-8">
            <Link to="/admin/courses" className="font-headline flex items-center gap-2 tracking-wider transition-opacity hover:opacity-90">
              <img src="/motionlab.png" alt="motionLAB Logo" className="h-10 w-auto object-contain" />
              <div className="flex items-center text-lg font-bold">
                <span className="text-foreground">motion</span>
                <span className="text-gradient ml-[-1px]">LAB</span>
                <span className="ml-2 text-xs font-medium text-muted-foreground uppercase tracking-widest">Admin</span>
              </div>
            </Link>

            <nav
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
              className="hidden min-w-0 flex-1 items-center gap-3 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden md:flex"
            >
              {primaryLinks.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  style={{ flexShrink: 0 }}
                  className={({ isActive }) =>
                    cn(
                      'rounded-md px-3 py-1.5 font-nav text-sm font-medium transition-all hover:text-primary',
                      isActive ? 'bg-primary/20 text-primary' : 'text-foreground/80 hover:bg-muted',
                    )
                  }
                >
                  {link.label}
                </NavLink>
              ))}
              {collapsedLinks.length > 0 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      style={{ flexShrink: 0 }}
                      className={cn(
                        'rounded-md px-3 py-1.5 font-nav text-sm font-medium transition-all hover:text-primary',
                        moreMenuActive
                          ? 'bg-primary/20 text-primary'
                          : 'text-foreground/80 hover:bg-muted',
                      )}
                    >
                      Mehr
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-56">
                    {collapsedLinks.map((link) => (
                      <DropdownMenuItem key={link.to} asChild>
                        <Link to={link.to}>{link.label}</Link>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </nav>
          </div>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button type="button" variant="outline" size="sm" className="md:hidden">
                  Menü
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                {mobileLinks.map((link) => (
                  <DropdownMenuItem key={link.to} asChild>
                    <Link to={link.to}>{link.label}</Link>
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onSelect={() => logoutMutation.mutate()}
                  disabled={logoutMutation.isPending}
                >
                  {logoutMutation.isPending ? 'Logout ...' : 'Logout'}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="hidden font-nav text-sm font-medium text-foreground/80 transition-all hover:bg-muted hover:text-primary md:inline-flex"
              onClick={() => logoutMutation.mutate()}
              disabled={logoutMutation.isPending}
            >
              Logout
            </Button>
          </div>
        </PageContainer>
      </header>
      <main className="flex-1 min-h-0">
        <PageContainer className="h-full overflow-y-auto py-8">
          <Outlet />
        </PageContainer>
      </main>
      <div className="shrink-0"><Footer showGitHash /></div>
    </div>
  )
}
